/**
 * @file OnboardingPage.js
 * @description Represents the initial onboarding page component for company code entry and profile verification.
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import VerifyProfile from './VerifyProfileForm';
import { useCompanyConfig } from './../../hooks/useCompanyConfig';
import styles from './OnboardingPage.module.scss';
import Spinner from '../ui/spinners/Spinner';
import { useTheme } from '../../store/ThemeContext';
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import CompanyCodeForm from '../auth/CompanyCodeForm';
import HeightScaledContent from '../ScaledContent/HeightScaledContent';
import PlaceholderLogo from '../ui/PlaceholderLogo';
import { ROUTE_BUILDERS } from '../../constants/routes';
import { getText } from '../../i18n';
const OnboardingPage = () => {
    const themeStyles = useTheme();
    const { isCompanyCodeReady, authData } = useCompanyConfig();
    const { companyCode } = authData;
    const { companyConfig = null } = authData;
    const { application = null } = companyConfig || {};
    const [onboardingError, setOnboardingError] = useState(null);
    const navigate = useNavigate();

    const [identifiers, setIdentifiers] = useState(null);
    const [hasIdentifiers, setHasIdentifiers] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [baseHeight, setBaseHeight] = useState(900);
    const contentRef = useRef(null);

    useEffect(() => {
        const checkMobileDevice = () => {
            setIsMobile(window.innerWidth < 768);
        };
        checkMobileDevice();
        window.addEventListener('resize', checkMobileDevice);
        return () => window.removeEventListener('resize', checkMobileDevice);
    }, []);

    const handleOnboardingError = errorMessage => {
        setOnboardingError(errorMessage);
    };

    const onProfileVerify = data => {
        if (data.isSelfRegistration) {
            navigate(
                `/onboarding/self-register${
                    data.companyCode ? `?companyCode=${data.companyCode}` : ''
                }`,
                {
                    state: {
                        profileData: data.profileData,
                        companyCode: data.companyCode,
                        isSelfRegistration: true,
                    },
                },
            );
        } else {
            window.location.href = `/onboarding/${data.onboardingCode}?companyCode=${data.companyCode}`;
        }
    };

    const handleCompanyCodeSubmit = submittedCompanyCode => {
        const currentPath = window.location.pathname;
        const newUrl = `${currentPath}?companyCode=${submittedCompanyCode}`;
        window.location.href = newUrl;
    };

    const background = () => css`
        ${themeStyles?.loginBackground?.type === 'color'
            ? `background: ${themeStyles?.loginBackground?.color}`
            : `background-image: url(${themeStyles?.loginBackground?.image})`}
    `;

    const handleBackToLogin = () => {
        navigate(ROUTE_BUILDERS.loginWithCompanyCode(companyCode));
    };

    useEffect(() => {
        if (companyConfig?.application?.identifiers?.length) {
            setIdentifiers(application?.identifiers || []);
        }
    }, [companyConfig, application]);

    useEffect(() => {
        if (identifiers?.length) {
            setHasIdentifiers(true);
        }
    }, [identifiers]);

    useEffect(() => {
        const calculateContentHeight = () => {
            if (contentRef.current && !isMobile) {
                const height = contentRef.current.scrollHeight;
                if (height > 0) {
                    setBaseHeight(height);
                }
            }
        };

        calculateContentHeight();

        const timeoutId = setTimeout(calculateContentHeight, 100);

        return () => clearTimeout(timeoutId);
    }, [isCompanyCodeReady, companyConfig, identifiers, isMobile]);

    const isSelfRegistrationEnabled =
        companyConfig?.application?.self_registration || false;

    return (
        <div className={`${styles.container}`} css={background}>
            <HeightScaledContent baseHeight={baseHeight}>
                <div
                    ref={contentRef}
                    className={`${styles.authBox} col-12 col-sm-12 col-md-7 col-lg-5 m-auto p-4`}
                >
                    <div
                        className={`${styles.errorContainer} ${
                            onboardingError ? '' : 'invisible'
                        }`}
                    >
                        <p className="text-danger">{onboardingError}</p>
                    </div>

                    {!isCompanyCodeReady ? (
                        <Spinner width={50} className={styles.loadingSpinner} />
                    ) : (
                        <div
                            className={`${!isMobile ? 'ing-card' : ''} ${styles.authContainer} card modal-content py-5`}
                        >
                            <small
                                className={`${styles.returnToLoginButton} position-absolute`}
                                onClick={handleBackToLogin}
                            >
                                <i className="bi bi-chevron-left ms-3" /> {getText('msg_b5cd32158b70')}
                            </small>
                            <div
                                className={`form-group text-center my-3 ${styles.logoContainer}`}
                            >
                                {themeStyles?.primaryLogo ? (
                                    <img
                                        src={themeStyles.primaryLogo}
                                        alt={getText('msg_5807dd602664')}
                                    />
                                ) : (
                                    <PlaceholderLogo />
                                )}
                                {companyConfig && hasIdentifiers && (
                                    <small className="d-block w-100 mb-2">
                                        {getText('msg_fe744924dc46')} 
                                        <a href={`/login`} className="pl-2">
                                            {getText('msg_e84712fbf4ee')}
                                        </a>
                                    </small>
                                )}
                            </div>

                            {!companyConfig ? (
                                <CompanyCodeForm
                                    onSubmit={handleCompanyCodeSubmit}
                                    initialCompanyCode=""
                                />
                            ) : (
                                <VerifyProfile
                                    onVerificationComplete={onProfileVerify}
                                    handleOnboardingError={
                                        handleOnboardingError
                                    }
                                    isSelfRegistration={
                                        isSelfRegistrationEnabled
                                    }
                                    authData={{
                                        ...authData,
                                        ...(themeStyles.tenantId
                                            ? {
                                                  tenantId:
                                                      themeStyles.tenantId,
                                              }
                                            : {}),
                                    }}
                                />
                            )}
                        </div>
                    )}
                </div>
            </HeightScaledContent>
        </div>
    );
};

export default OnboardingPage;
